<template>
  <div
    class="flex items-center justify-between gap-3 rounded-[var(--plugin-radius)] bg-[var(--plugin-gray-100)] p-3"
    style="border-radius: var(--plugin-card-radius);"
  >
    <div class="flex min-w-0 flex-1 items-center gap-4">
      <div
        class="flex shrink-0 items-center justify-center rounded-[var(--plugin-radius)] bg-card py-2.5 px-3"
        style="border-radius: var(--plugin-radius);"
      >
        <span
          class="flex [&_svg]:h-5 [&_svg]:w-5"
          :class="props.allStepsCompleted || state.submitted ? 'text-[var(--plugin-charcoal)]' : 'text-primary'"
        >
          <component :is="currentStepItem.icon" />
        </span>
      </div>
      <span
        class="truncate text-sm font-medium"
        :class="props.allStepsCompleted || state.submitted ? 'text-[var(--plugin-charcoal)]' : 'text-primary'"
      >
        {{ currentStepItem.label }}
      </span>
    </div>
    <div class="flex shrink-0 items-center gap-3">
      <div
        class="h-0.5 w-full min-w-[40px] max-w-[120px] overflow-hidden rounded-full bg-[var(--plugin-progress-track)]"
      >
        <div
          class="h-full rounded-full bg-primary transition-[width] duration-200"
          :style="{ width: `${progressPercent}%` }"
        />
      </div>
      <span class="shrink-0 text-xs font-bold">
        <span class="text-[var(--plugin-charcoal)]">{{ currentStepNumber }}</span>
        <span class="text-[var(--plugin-gray-500)]">/{{ visibleSteps.length }}</span>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, h } from 'vue'
import { useWizardState } from '@/composables/useWizardState'
import { t } from '@/i18n'
import type { WizardStep } from '@/composables/useWizardState'
import { createStepperIcons } from '@/components/StepperIcons'

const fullStepOrder: WizardStep[] = [
  'branches',
  'products',
  'datetime',
  'employees',
  'customer',
  'summary',
]

const stepperIcons = createStepperIcons(h)

const isWorkshop = computed(
  () =>
    state.product?.type === 'workshop' ||
    (state.product as { is_workshop?: boolean } | null)?.is_workshop === true
)

const stepOrder = computed(() =>
  isWorkshop.value
    ? fullStepOrder.filter((s) => s !== 'datetime' && s !== 'employees')
    : fullStepOrder
)

const stepConfig: Record<WizardStep, { labelKey: string; icon: () => ReturnType<typeof h> }> = {
  branches: { labelKey: 'plugin.steps.branches', icon: stepperIcons.branch },
  products: { labelKey: 'plugin.steps.products', icon: stepperIcons.service },
  datetime: { labelKey: 'plugin.steps.datetime', icon: stepperIcons.date },
  employees: { labelKey: 'plugin.steps.employees', icon: stepperIcons.employees },
  customer: { labelKey: 'plugin.steps.customer', icon: stepperIcons.personalData },
  summary: { labelKey: 'plugin.steps.summary', icon: stepperIcons.personalData },
  payment: { labelKey: 'plugin.steps.payment', icon: stepperIcons.service },
}

const props = defineProps<{ allStepsCompleted?: boolean }>()
const state = useWizardState()

const visibleSteps = computed(() =>
  stepOrder.value.map((key) => ({
    key,
    label: t(stepConfig[key].labelKey),
    icon: stepConfig[key].icon,
  }))
)

const currentStepIndex = computed(() => {
  if (props.allStepsCompleted) return Math.max(0, visibleSteps.value.length - 1)
  const order = stepOrder.value
  const idx = order.indexOf(state.step)
  if (idx >= 0) return idx
  if (state.step === 'payment') return order.length - 1
  return 0
})

const currentStepNumber = computed(() =>
  props.allStepsCompleted ? visibleSteps.value.length : currentStepIndex.value + 1
)

const progressPercent = computed(() => {
  const n = visibleSteps.value.length
  if (n <= 0) return 0
  if (props.allStepsCompleted) return 100
  return ((currentStepIndex.value + 1) / n) * 100
})

const currentStepItem = computed(() => visibleSteps.value[currentStepIndex.value] ?? visibleSteps.value[0])
</script>
